import type React from "react";
import { Chip } from "@mui/material";

type LocationColorConfig = {
  bg: string;
  text: string;
  variant: "solid" | "outlinedLight" | "outlinedDark";
};

// Extended palette with solid and alternate bordered variants for distinction.
const LOCATION_COLOR_CONFIGS: LocationColorConfig[] = [
  // Blue shades
  { bg: "#2196F3", text: "#FFFFFF", variant: "solid" }, // Blue
  { bg: "#1976D2", text: "#FFFFFF", variant: "solid" }, // Dark Blue
  { bg: "#42A5F5", text: "#FFFFFF", variant: "solid" }, // Light Blue
  { bg: "#0D47A1", text: "#FFFFFF", variant: "solid" }, // Deep Blue
  { bg: "#1565C0", text: "#FFFFFF", variant: "solid" }, // Medium Blue
  { bg: "#64B5F6", text: "#000000", variant: "solid" }, // Very Light Blue
  { bg: "#0277BD", text: "#FFFFFF", variant: "solid" }, // Bright Blue
  { bg: "#0288D1", text: "#FFFFFF", variant: "solid" }, // Cyan Blue
  { bg: "#2196F3", text: "#FFFFFF", variant: "outlinedLight" }, // Blue Light
  { bg: "#1976D2", text: "#FFFFFF", variant: "outlinedDark" }, // Dark Blue Dark
  { bg: "#42A5F5", text: "#FFFFFF", variant: "outlinedLight" }, // Light Blue Light
  { bg: "#0D47A1", text: "#FFFFFF", variant: "outlinedDark" }, // Deep Blue Dark
  { bg: "#1565C0", text: "#FFFFFF", variant: "outlinedLight" }, // Medium Blue Light
  { bg: "#64B5F6", text: "#000000", variant: "outlinedDark" }, // Very Light Blue Dark
  { bg: "#0277BD", text: "#FFFFFF", variant: "outlinedLight" }, // Bright Blue Light
  { bg: "#0288D1", text: "#FFFFFF", variant: "outlinedDark" }, // Cyan Blue Dark

  // Green shades
  { bg: "#4CAF50", text: "#FFFFFF", variant: "solid" }, // Green
  { bg: "#388E3C", text: "#FFFFFF", variant: "solid" }, // Dark Green
  { bg: "#66BB6A", text: "#FFFFFF", variant: "solid" }, // Light Green
  { bg: "#1B5E20", text: "#FFFFFF", variant: "solid" }, // Deep Green
  { bg: "#2E7D32", text: "#FFFFFF", variant: "solid" }, // Medium Green
  { bg: "#81C784", text: "#000000", variant: "solid" }, // Very Light Green
  { bg: "#43A047", text: "#FFFFFF", variant: "solid" }, // Bright Green
  { bg: "#558B2F", text: "#FFFFFF", variant: "solid" }, // Olive Green
  { bg: "#4CAF50", text: "#FFFFFF", variant: "outlinedLight" }, // Green Light
  { bg: "#388E3C", text: "#FFFFFF", variant: "outlinedDark" }, // Dark Green Dark
  { bg: "#66BB6A", text: "#FFFFFF", variant: "outlinedLight" }, // Light Green Light
  { bg: "#1B5E20", text: "#FFFFFF", variant: "outlinedDark" }, // Deep Green Dark
  { bg: "#2E7D32", text: "#FFFFFF", variant: "outlinedLight" }, // Medium Green Light
  { bg: "#81C784", text: "#000000", variant: "outlinedDark" }, // Very Light Green Dark
  { bg: "#43A047", text: "#FFFFFF", variant: "outlinedLight" }, // Bright Green Light
  { bg: "#558B2F", text: "#FFFFFF", variant: "outlinedDark" }, // Olive Green Dark

  // Red/Orange shades
  { bg: "#F44336", text: "#FFFFFF", variant: "solid" }, // Red
  { bg: "#D32F2F", text: "#FFFFFF", variant: "solid" }, // Dark Red
  { bg: "#FF5722", text: "#FFFFFF", variant: "solid" }, // Deep Orange
  { bg: "#E64A19", text: "#FFFFFF", variant: "solid" }, // Dark Orange
  { bg: "#C62828", text: "#FFFFFF", variant: "solid" }, // Deep Red
  { bg: "#EF5350", text: "#FFFFFF", variant: "solid" }, // Light Red
  { bg: "#FF7043", text: "#FFFFFF", variant: "solid" }, // Bright Orange
  { bg: "#FF6F00", text: "#FFFFFF", variant: "solid" }, // Amber Orange
  { bg: "#F44336", text: "#FFFFFF", variant: "outlinedLight" }, // Red Light
  { bg: "#D32F2F", text: "#FFFFFF", variant: "outlinedDark" }, // Dark Red Dark
  { bg: "#FF5722", text: "#FFFFFF", variant: "outlinedLight" }, // Deep Orange Light
  { bg: "#E64A19", text: "#FFFFFF", variant: "outlinedDark" }, // Dark Orange Dark
  { bg: "#C62828", text: "#FFFFFF", variant: "outlinedLight" }, // Deep Red Light
  { bg: "#EF5350", text: "#FFFFFF", variant: "outlinedDark" }, // Light Red Dark
  { bg: "#FF7043", text: "#FFFFFF", variant: "outlinedLight" }, // Bright Orange Light
  { bg: "#FF6F00", text: "#FFFFFF", variant: "outlinedDark" }, // Amber Orange Dark

  // Purple/Violet shades
  { bg: "#9C27B0", text: "#FFFFFF", variant: "solid" }, // Purple
  { bg: "#7B1FA2", text: "#FFFFFF", variant: "solid" }, // Dark Purple
  { bg: "#BA68C8", text: "#FFFFFF", variant: "solid" }, // Light Purple
  { bg: "#4A148C", text: "#FFFFFF", variant: "solid" }, // Deep Purple
  { bg: "#6A1B9A", text: "#FFFFFF", variant: "solid" }, // Medium Purple
  { bg: "#CE93D8", text: "#000000", variant: "solid" }, // Very Light Purple
  { bg: "#AB47BC", text: "#FFFFFF", variant: "solid" }, // Bright Purple
  { bg: "#8E24AA", text: "#FFFFFF", variant: "solid" }, // Violet
  { bg: "#9C27B0", text: "#FFFFFF", variant: "outlinedLight" }, // Purple Light
  { bg: "#7B1FA2", text: "#FFFFFF", variant: "outlinedDark" }, // Dark Purple Dark
  { bg: "#BA68C8", text: "#FFFFFF", variant: "outlinedLight" }, // Light Purple Light
  { bg: "#4A148C", text: "#FFFFFF", variant: "outlinedDark" }, // Deep Purple Dark
  { bg: "#6A1B9A", text: "#FFFFFF", variant: "outlinedLight" }, // Medium Purple Light
  { bg: "#CE93D8", text: "#000000", variant: "outlinedDark" }, // Very Light Purple Dark
  { bg: "#AB47BC", text: "#FFFFFF", variant: "outlinedLight" }, // Bright Purple Light
  { bg: "#8E24AA", text: "#FFFFFF", variant: "outlinedDark" }, // Violet Dark

  // Yellow/Amber shades
  { bg: "#FFC107", text: "#000000", variant: "solid" }, // Amber
  { bg: "#FFA000", text: "#FFFFFF", variant: "solid" }, // Dark Amber
  { bg: "#FFD54F", text: "#000000", variant: "solid" }, // Light Amber
  { bg: "#F9A825", text: "#000000", variant: "solid" }, // Golden Yellow
  { bg: "#FFC107", text: "#000000", variant: "outlinedLight" }, // Amber Light
  { bg: "#FFA000", text: "#FFFFFF", variant: "outlinedDark" }, // Dark Amber Dark
  { bg: "#FFD54F", text: "#000000", variant: "outlinedLight" }, // Light Amber Light
  { bg: "#F9A825", text: "#000000", variant: "outlinedDark" }, // Golden Yellow Dark

  // Teal/Cyan shades
  { bg: "#009688", text: "#FFFFFF", variant: "solid" }, // Teal
  { bg: "#00796B", text: "#FFFFFF", variant: "solid" }, // Dark Teal
  { bg: "#26A69A", text: "#FFFFFF", variant: "solid" }, // Light Teal
  { bg: "#00ACC1", text: "#FFFFFF", variant: "solid" }, // Cyan
  { bg: "#009688", text: "#FFFFFF", variant: "outlinedLight" }, // Teal Light
  { bg: "#00796B", text: "#FFFFFF", variant: "outlinedDark" }, // Dark Teal Dark
  { bg: "#26A69A", text: "#FFFFFF", variant: "outlinedLight" }, // Light Teal Light
  { bg: "#00ACC1", text: "#FFFFFF", variant: "outlinedDark" }, // Cyan Dark

  // Pink shades
  { bg: "#E91E63", text: "#FFFFFF", variant: "solid" }, // Pink
  { bg: "#C2185B", text: "#FFFFFF", variant: "solid" }, // Dark Pink
  { bg: "#EC407A", text: "#FFFFFF", variant: "solid" }, // Light Pink
  { bg: "#AD1457", text: "#FFFFFF", variant: "solid" }, // Deep Pink
  { bg: "#E91E63", text: "#FFFFFF", variant: "outlinedLight" }, // Pink Light
  { bg: "#C2185B", text: "#FFFFFF", variant: "outlinedDark" }, // Dark Pink Dark
  { bg: "#EC407A", text: "#FFFFFF", variant: "outlinedLight" }, // Light Pink Light
  { bg: "#AD1457", text: "#FFFFFF", variant: "outlinedDark" }, // Deep Pink Dark

  // Brown/Grey/Indigo shades
  { bg: "#795548", text: "#FFFFFF", variant: "solid" }, // Brown
  { bg: "#607D8B", text: "#FFFFFF", variant: "solid" }, // Blue Grey
  { bg: "#5C6BC0", text: "#FFFFFF", variant: "solid" }, // Indigo
  { bg: "#3F51B5", text: "#FFFFFF", variant: "solid" }, // Deep Indigo
  { bg: "#795548", text: "#FFFFFF", variant: "outlinedLight" }, // Brown Light
  { bg: "#607D8B", text: "#FFFFFF", variant: "outlinedDark" }, // Blue Grey Dark
  { bg: "#5C6BC0", text: "#FFFFFF", variant: "outlinedLight" }, // Indigo Light
  { bg: "#3F51B5", text: "#FFFFFF", variant: "outlinedDark" }, // Deep Indigo Dark
];

// Improved hash function (djb2 algorithm) for better distribution
const getLocationHash = (locationName: string): number => {
  let hash = 5381;
  const normalizedName = locationName.toLowerCase().trim();
  for (let i = 0; i < normalizedName.length; i++) {
    hash = (hash << 5) + hash + normalizedName.charCodeAt(i);
  }
  return Math.abs(hash);
};

type LocationColorStyle = {
  bg: string;
  border: string;
  text: string;
};

// Generate consistent color for a location name
const getLocationColor = (locationName: string): LocationColorStyle => {
  const hash = getLocationHash(locationName);
  const index = hash % LOCATION_COLOR_CONFIGS.length;
  const colorConfig = LOCATION_COLOR_CONFIGS[index];

  if (colorConfig.variant === "outlinedLight" || colorConfig.variant === "outlinedDark") {
    return {
      bg: colorConfig.variant === "outlinedLight" ? "#FFFFFF" : "#000000",
      border: colorConfig.bg,
      text: colorConfig.variant === "outlinedLight" ? "#000000" : "#FFFFFF",
    };
  }

  return {
    bg: colorConfig.bg,
    border: colorConfig.bg,
    text: colorConfig.text,
  };
};

export interface SyncableChipProps {
  label: string;
  hoveredValue?: string | null;
  filteredValue?: string | null;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
}

const SyncableChip: React.FC<SyncableChipProps> = ({
  label,
  hoveredValue = null,
  filteredValue = null,
  onMouseEnter,
  onMouseLeave,
  onClick,
}) => {
  const colorConfig = getLocationColor(label);
  const isHighlighted = hoveredValue === label;
  const isFiltered = filteredValue === label;

  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        backgroundColor: colorConfig.bg,
        backgroundImage: "none",
        border: "2px solid",
        borderColor: colorConfig.border,
        color: colorConfig.text,
        fontWeight: 700,
        fontSize: "0.75rem",
        height: "20px",
        "& .MuiChip-label": {
          padding: "0 8px",
          fontSize: "0.75rem",
          fontWeight: 700,
        },
        transform: isHighlighted ? "scale(1.1)" : isFiltered ? "scale(1.15)" : "scale(1)",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        boxShadow: isHighlighted
          ? "0 4px 8px rgba(0, 0, 0, 0.3), 0 0 0 2px rgba(255, 255, 255, 0.5)"
          : isFiltered
            ? "0 4px 12px rgba(0, 0, 0, 0.4), 0 0 0 3px rgba(255, 255, 255, 0.8)"
            : "none",
        zIndex: isHighlighted || isFiltered ? 1 : 0,
        cursor: "pointer",
        "&:hover": {
          transform: "scale(1.05)",
          backgroundColor: colorConfig.bg,
          backgroundImage: "none",
        },
        "&:active": {
          backgroundColor: colorConfig.bg,
          backgroundImage: "none",
        },
      }}
    />
  );
};

export default SyncableChip;
